<template>
    <Message v-bind="primeVueMessageProps" @close="toggleMessage">
        <slot />
    </Message>
</template>

<script>
import { computed, ref, useAttrs, watch } from 'vue';
/**
 * EMessage - Abstraction wrapper around PrimeVue's Message.
 */
export default {
    name: 'EMessage',
    inheritAttrs: false,
    emits: ['close', 'update:active'],
    props: {
        type: {
            type: String,
            default: 'is-info',
        },
        title: {
            type: String,
            default: null,
        },
        closable: {
            type: Boolean,
            default: false,
        },
        hasIcon: {
            type: Boolean,
            default: false,
        },
        duration: {
            type: Number,
            default: undefined,
        },
        active: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const attrs = useAttrs();

        const localActive = ref(props.active);

        watch(
            () => props.active,
            val => {
                localActive.value = val;
            }
        );

        const computedActive = computed({
            get: () => localActive.value,
            set: val => {
                localActive.value = val;
                emit('update:active', val);
            },
        });

        const toggleMessage = () => {
            computedActive.value = false;
            emit('close');
        };

        const severityMap = {
            'is-info': 'info',
            'is-success': 'success',
            'is-warning': 'warn',
            'is-danger': 'error',
        };

        const primeVueMessageProps = computed(() => {
            const { type, closable, hasIcon, icon, duration, ...rest } = { ...attrs, ...props };
            const severity = severityMap[type] || 'info';

            let newIcon = icon;
            if (icon && !icon.includes('pi') && !icon.includes('uil')) {
                newIcon = `uil uil-${icon}`;
            }

            return {
                ...rest,
                severity,
                closable,
                life: duration,
                icon: hasIcon ? newIcon : undefined,
            };
        });

        return {
            primeVueMessageProps,
            computedActive,
            toggleMessage,
            ...props,
        };
    },
};
</script>

<style scoped>
/* Fix vertical alignment of icon and text in message body */
:deep(.media) {
    align-items: center;
}
</style>
